import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_BANK_DETAILS } from '../data/constants';
import { useAuth } from './AuthContext';
import { apiRequest } from '../utils/api';
import { getIdempotencyKey, releaseIdempotencyKey, shouldRetainIdempotencyKey } from '../utils/idempotency';
import { readCachedValue, readStorageJSON, writeCachedValue } from '../utils/storage';

const BankSettingsContext = createContext(null);
const BANK_CACHE_KEY = 'payrocky_bank_details';
const BANK_CACHE_TTL_MS = 60 * 1000;
const BANK_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

function isAuthRoute() {
  if (typeof window === 'undefined') return false;
  const pathname = String(window.location.pathname || '').toLowerCase();
  return pathname === '/login'
    || pathname === '/signup'
    || pathname === '/forgot-password'
    || pathname.startsWith('/reset-password/');
}

function normalizeBankDetails(source = {}) {
  return {
    bank_name: source.bank_name || DEFAULT_BANK_DETAILS.bank_name || '',
    account_name: source.account_name || DEFAULT_BANK_DETAILS.account_name || '',
    account_number: source.account_number || DEFAULT_BANK_DETAILS.account_number || '',
  };
}

function readStaleBankDetails() {
  const cached = readStorageJSON(BANK_CACHE_KEY, null);
  if (!cached || typeof cached !== 'object') return null;
  return cached.value && typeof cached.value === 'object' ? cached.value : null;
}

function canActivelyRefreshBankDetails() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

export function BankSettingsProvider({ children }) {
  const { currentUser, token, loading: authLoading } = useAuth();
  const [bankDetails, setBankDetails] = useState(() => normalizeBankDetails(readCachedValue(BANK_CACHE_KEY, BANK_CACHE_TTL_MS, DEFAULT_BANK_DETAILS)));

  const refreshBankDetails = useCallback(async (force = false) => {
    if (!force) {
      const cachedBankDetails = readCachedValue(BANK_CACHE_KEY, BANK_CACHE_TTL_MS, null);
      if (cachedBankDetails) {
        const next = normalizeBankDetails(cachedBankDetails);
        setBankDetails(next);
      }
    }

    const path = currentUser?.role === 'admin' ? '/admin/bank-details' : '/bank-details';
    const res = await apiRequest(path, currentUser?.role === 'admin' ? { token, timeoutMs: 10000 } : { timeoutMs: 10000 });
    const next = normalizeBankDetails(res.bankDetails);
    setBankDetails(next);
    writeCachedValue(BANK_CACHE_KEY, next);
    return next;
  }, [currentUser?.role, token]);

  useEffect(() => {
    if (isAuthRoute()) return;
    if (authLoading && token) return;
    refreshBankDetails().catch(() => {
      const cachedBankDetails = readCachedValue(BANK_CACHE_KEY, BANK_CACHE_TTL_MS, null);
      if (cachedBankDetails) {
        setBankDetails(normalizeBankDetails(cachedBankDetails));
        return;
      }
      const staleBankDetails = readStaleBankDetails();
      if (staleBankDetails) {
        setBankDetails(normalizeBankDetails(staleBankDetails));
        return;
      }
      setBankDetails((prev) => normalizeBankDetails(prev && Object.keys(prev).length ? prev : DEFAULT_BANK_DETAILS));
    });
  }, [authLoading, refreshBankDetails, token]);

  useEffect(() => {
    if (isAuthRoute()) return undefined;
    if (authLoading && token) return undefined;

    const refreshIfVisible = () => {
      if (!canActivelyRefreshBankDetails()) return;
      refreshBankDetails(true).catch(() => {});
    };

    const intervalId = window.setInterval(refreshIfVisible, BANK_REFRESH_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfVisible();
      }
    };
    const handleWindowFocus = () => {
      refreshIfVisible();
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authLoading, refreshBankDetails, token]);

  const hasCompleteBankDetails = Boolean(
    bankDetails.bank_name && bankDetails.account_name && bankDetails.account_number
  );

  const adminUpdateBankDetails = async (payload) => {
    const normalizedPayload = normalizeBankDetails(payload);
    const idempotencyKey = getIdempotencyKey('admin:update-bank-details', normalizedPayload);
    try {
      const res = await apiRequest('/admin/update-bank-details', {
        method: 'POST',
        token,
        timeoutMs: 10000,
        idempotencyKey,
        body: normalizedPayload,
      });
      releaseIdempotencyKey('admin:update-bank-details', normalizedPayload);
      const next = normalizeBankDetails(res.bankDetails);
      setBankDetails(next);
      writeCachedValue(BANK_CACHE_KEY, next);
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:update-bank-details', normalizedPayload);
      }
      throw error;
    }
  };

  const value = useMemo(() => ({
    bankDetails,
    hasCompleteBankDetails,
    refreshBankDetails,
    adminUpdateBankDetails,
  }), [bankDetails, hasCompleteBankDetails, refreshBankDetails]);

  return <BankSettingsContext.Provider value={value}>{children}</BankSettingsContext.Provider>;
}

export function useBankSettings() {
  const ctx = useContext(BankSettingsContext);
  if (!ctx) throw new Error('useBankSettings must be used inside <BankSettingsProvider>');
  return ctx;
}
